/**
 * dataStats — a tiny module-level tally of the reactive data the app has received,
 * so the raw broker logs ("readDoc → 9 rows") gain a human-readable, app-level
 * companion: how many objects of WHICH shape have landed across every query cycle.
 *
 * It is fed by `useShapeQuery` on each first-result transition (isPending →
 * success) — one call per received set — and prints a concise recap so the
 * developer sees "how many events / participations / profiles in total" at a
 * glance, without decoding the low-level access log.
 *
 * Plain module singleton (like pendingQueries.ts): no React coupling, safe to call
 * from anywhere. Counters are CUMULATIVE across the session (they only grow), which
 * is intentional — it answers "how much data flowed", not "current set size".
 */

/** A short, readable shape name from a shape URI ("…/Event" → "Event"). */
export function shapeLabel(shapeKey: string): string {
  const m = /[/#]([^/#]+)$/.exec(shapeKey);
  return m?.[1] ?? shapeKey;
}

interface Stats {
  /** Number of sets (query first-results) received, per shape label. */
  sets: Map<string, number>;
  /** Total objects received (summed across sets), per shape label. */
  objects: Map<string, number>;
}

const stats: Stats = { sets: new Map(), objects: new Map() };

/** Record one received set of `count` objects of shape `label`. */
export function recordSet(label: string, count: number): void {
  stats.sets.set(label, (stats.sets.get(label) ?? 0) + 1);
  stats.objects.set(label, (stats.objects.get(label) ?? 0) + count);
}

/** A concise "Label: N" recap of cumulative objects received, sorted by label. */
export function totalsSummary(): string {
  const parts = [...stats.objects.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, n]) => `${label}: ${n}`);
  return parts.join(', ') || '(aucun)';
}

/** Total number of sets received across all shapes (for the recap line). */
export function totalSets(): number {
  let n = 0;
  for (const c of stats.sets.values()) n += c;
  return n;
}

/** Reset the tally (test hook — never called by the app). */
export function resetDataStats(): void {
  stats.sets.clear();
  stats.objects.clear();
}
